import { ActivityType } from '../enums/ActivityType'
import { WherebyCallClaimStatus, WherebyCallKind, WherebyCallOwnershipStatus } from '../enums/Whereby'
import { activityUsesSprintRoom } from './activityUsesDedicatedRoom'
import type { WherebyCallOwnershipDoc, WherebyCallScope } from '../types/WherebyTypes'

// One live Whereby connection per user, shared FE + BE rules.
//
// The owner refreshes its heartbeat on this cadence while connected. A claim whose heartbeat has lapsed
// belongs to a tab that is gone (closed, crashed, or asleep) and may be taken without asking. A tab that
// is really in a call keeps heartbeating — browsers exempt pages with live WebRTC/audio from background
// timer throttling — so a lapsed heartbeat can never silence a call the user is actually in.
// The stale window is 3+ missed heartbeats, and the cadence is kept low on purpose: one request per
// 15 s per user IN A CALL is the whole cost of this feature.
export const WHEREBY_OWNERSHIP_HEARTBEAT_MS = 15_000
export const WHEREBY_OWNERSHIP_STALE_MS = 50_000
// While BLOCKED the surface quietly re-asks on this cadence, so the call connects by itself once the
// other holder's heartbeat lapses — instead of sitting on the prompt after that tab is already gone.
export const WHEREBY_BLOCKED_RECLAIM_MS = 10_000 // 10 s

// Activity types that run in the game's own room. Shared so route gating, scope resolution and the
// ownership prompt all agree on what "a game call" is.
export const WHEREBY_GAME_ACTIVITY_TYPES = new Set<string>([
  ActivityType.FIRST_GAME,
  ActivityType.SHORT_GAME,
  ActivityType.LONG_GAME
])

// The whole rule, in one place: you get the call slot if it is free, if it is already yours, if the
// previous owner is gone, or if YOU confirmed the takeover. Never otherwise — that is what stops a second
// tab from silently ending a call the user is in.
export const decideWherebyCallOwnership = (
  current: WherebyCallOwnershipDoc | undefined,
  request: { clientId: string; takeover?: boolean },
  nowMs: number
): WherebyCallClaimStatus => {
  if (!current) return WherebyCallClaimStatus.GRANTED
  if (current.clientId === request.clientId) return WherebyCallClaimStatus.GRANTED
  if (request.takeover) return WherebyCallClaimStatus.GRANTED

  // A missing/corrupt heartbeat counts as gone rather than locking the user out forever.
  const heartbeatAgeMs = current.heartbeatAt ? nowMs - new Date(current.heartbeatAt).getTime() : Number.MAX_SAFE_INTEGER
  const ownerIsGone = Number.isNaN(heartbeatAgeMs) || heartbeatAgeMs > WHEREBY_OWNERSHIP_STALE_MS
  return ownerIsGone ? WherebyCallClaimStatus.GRANTED : WherebyCallClaimStatus.BLOCKED
}

// The states in which a call surface must ASK the user instead of connecting: the call is live on another
// tab/device, another surface in this tab has it, or we could not verify it. Connecting in any of these
// would mean two live connections (or connecting on an unknown state).
export type WherebyStatusRequiringAsk =
  | WherebyCallOwnershipStatus.BLOCKED
  | WherebyCallOwnershipStatus.BLOCKED_IN_TAB
  | WherebyCallOwnershipStatus.ERROR

const STATUSES_REQUIRING_ASK = new Set<WherebyCallOwnershipStatus>([
  WherebyCallOwnershipStatus.BLOCKED,
  WherebyCallOwnershipStatus.BLOCKED_IN_TAB,
  WherebyCallOwnershipStatus.ERROR
])

export const shouldAskBeforeConnectingWhereby = (
  status: WherebyCallOwnershipStatus
): status is WherebyStatusRequiringAsk => STATUSES_REQUIRING_ASK.has(status)

// Which kind of call a resolved activity runs: games and the AI-coach debrief have their own room, the
// introduction and the group reflection the sprint's room, everything else shares the team call.
export const resolveWherebyCallKind = (activityType?: string | null): WherebyCallKind => {
  if (activityType && WHEREBY_GAME_ACTIVITY_TYPES.has(activityType)) return WherebyCallKind.GAME
  if (activityType === ActivityType.SHORT_REFLECTION) return WherebyCallKind.AI_COACH
  if (activityUsesSprintRoom(activityType)) return WherebyCallKind.SPRINT
  return WherebyCallKind.TEAM
}

// A team can be sent to its game or its AI debrief; a sprint call is the whole sprint's, a team call has no page.
const CALL_KINDS_A_TEAM_IS_SENT_TO = new Set<WherebyCallKind>([WherebyCallKind.GAME, WherebyCallKind.AI_COACH])

export const canSendTeamToCall = (kind: WherebyCallKind): boolean => CALL_KINDS_A_TEAM_IS_SENT_TO.has(kind)

// A game call, complete enough to be re-pinned and re-joined on another tab/device.
export interface WherebyTakeoverResume {
  route: string
  scope: WherebyCallScope & { sprintId: string; activityId: string; activityType: string }
}

// After a confirmed takeover: where the call we just moved here has to continue. Only a GAME call is
// resumed by navigating (it is the one call you cannot join from an arbitrary page); anything else simply
// connects on the page the user is already on. Never sends a phone into a game (game is desktop-only).
export const resolveTakeoverResume = (params: {
  previousScope?: WherebyCallScope
  currentRoute: string
  isMobile: boolean
}): WherebyTakeoverResume | null => {
  const { previousScope, currentRoute, isMobile } = params
  if (isMobile || previousScope?.kind !== WherebyCallKind.GAME) return null

  // A game call can only be resumed elsewhere when we know its page AND its activity.
  const { route, sprintId, activityId, activityType } = previousScope
  if (!route || !sprintId || !activityId || !activityType) return null
  if (route === currentRoute) return null

  return { route, scope: { ...previousScope, sprintId, activityId, activityType } }
}

// Browser / OS labels for the ownership record. Support-only: the takeover prompt never shows them, so a
// wrong guess is harmless. First matching pattern wins.
const BROWSER_LABEL_PATTERNS: Array<[RegExp, string]> = [
  [/edg\//i, 'Edge'],
  [/opr\/|opera/i, 'Opera'],
  [/chrome|crios/i, 'Chrome'],
  [/firefox|fxios/i, 'Firefox'],
  [/safari/i, 'Safari']
]

const OS_LABEL_PATTERNS: Array<[RegExp, string]> = [
  [/iphone|ipad|ipod/i, 'iOS'],
  [/android/i, 'Android'],
  [/windows/i, 'Windows'],
  [/mac os x|macintosh/i, 'macOS'],
  [/linux/i, 'Linux']
]

const matchLabel = (patterns: Array<[RegExp, string]>, userAgent: string, fallback: string): string =>
  patterns.find(([pattern]) => pattern.test(userAgent))?.[1] ?? fallback

// Short label for the ownership record, e.g. "Chrome on macOS" (stored for support, never shown).
export const buildWherebyDeviceLabel = (userAgent?: string): string => {
  const browser = matchLabel(BROWSER_LABEL_PATTERNS, userAgent ?? '', 'Browser')
  const os = matchLabel(OS_LABEL_PATTERNS, userAgent ?? '', '')
  return os ? `${browser} on ${os}` : browser
}
